'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { type GetBuyItAgainRecommendationsInput, getBuyItAgainRecommendations } from '@libs/api-proxy-client';
import { getCustomerUuid, usePageLayout } from '@libs/common-next';

const ONE_HOUR = 60 * 60 * 1000;

type UseQueryBuyItAgainOptions = {
    input: GetBuyItAgainRecommendationsInput;
    staleTime?: number;
};

export const useQueryBuyItAgain = ({
    input: { placement, minCount, maxCount, saleIndicator, excludes, inventoryList },
    staleTime = ONE_HOUR,
}: UseQueryBuyItAgainOptions) => {
    const { user } = usePageLayout();
    const customerUuid = getCustomerUuid(user);

    const queryKey = [
        'buy-it-again',
        customerUuid,
        placement,
        minCount,
        maxCount,
        saleIndicator,
        excludes.join(','),
        inventoryList.join(','),
    ];

    return useQuery({
        queryKey,
        queryFn: () =>
            getBuyItAgainRecommendations({
                placement,
                minCount,
                maxCount,
                saleIndicator,
                excludes,
                inventoryList,
            }),
        enabled: Boolean(customerUuid),
        staleTime,
        // Keeps prior successful data visible while a changed query key refetches.
        placeholderData: keepPreviousData,
        select: data => data.data,
    });
};
